import { I18n } from 'react-redux-i18n';

import handleError, {
  rawResponseCatcher,
  responseCatcher,
  toJsonError,
  unwrap,
} from './handleError';
import { ApiQueryParam, WsError } from './WsTypes';
import { getCache, hasCache, insertCache } from './networkCacheService';
import { getToken } from '../services/userService';

export function getAuthorization(): string {
  const token = getToken();
  return `Bearer ${token ? unwrap(getToken()) : ''}`;
}

export function toMessage(wsError: WsError): { [key: string]: string } {
  const args = {};
  if (!wsError.statusArguments) {
    return args;
  }
  wsError.statusArguments.forEach((value, key) => {
    const label = I18n.t(value);
    if (label !== null) {
      args[`field${key}`] = label;
    }
  });
  return args;
}

export const getFileNameFromContentDisposition = (blob: Response): string => {
  const header = blob.headers.get('content-disposition');
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(header);
  return matches[1].replace(/['"]/g, '');
};

const stringify = <T>(body: T): string => {
  if (typeof body === 'string') {
    return body;
  }
  return JSON.stringify(body);
};

const baseApiUrl = '/api';

export class HttpClient {
  apiExtensionUrl: string;

  headersValue: HeadersInit;

  methodValue: string;

  params: ApiQueryParam[];

  bodyValue: string | null | undefined;

  producedType: string;

  constructor(apiExtensionUrl: string, method = 'GET', producedType = 'application/json') {
    this.apiExtensionUrl = apiExtensionUrl;
    this.headersValue = {};
    this.methodValue = method;
    this.bodyValue = null;
    this.params = [];
    this.producedType = producedType;
  }

  headers(headers: HeadersInit): HttpClient {
    Object.assign(this.headersValue, headers);
    return this;
  }

  body(body: string): HttpClient {
    this.bodyValue = body;
    return this;
  }

  addParam(key: string, value: (string | number | boolean)): HttpClient {
    if (value !== undefined && value !== null) {
      this.params.push({ key, value: String(value) });
    }
    return this;
  }

  addParamList(key: string, values: (string | number | boolean)[]): HttpClient {
    if (values && values.length > 0) {
      values.forEach((value) => this.params.push({ key, value: String(value) }));
    }
    return this;
  }

  jsonBody<T>(objectBody: T): HttpClient {
    this.headers({ 'Content-Type': 'application/json' });
    this.body(stringify(objectBody));
    return this;
  }

  apiExtensionUrlWithParams(): string {
    const baseUrl = baseApiUrl + this.apiExtensionUrl;
    if (this.params.length > 0) {
      return baseUrl.concat('?').concat(this.params.map((param) => `${param.key}=${param.value}`).join('&'));
    }
    return baseUrl;
  }

  execute<T>(): Promise<T> {
    return fetch(this.apiExtensionUrlWithParams(), {
      headers: this.headersValue,
      method: this.methodValue,
      body: this.bodyValue,
    })
      .catch(rawResponseCatcher)
      .then(handleError);
  }
}

export class RestClient extends HttpClient {
  cached: boolean;

  constructor(apiExtensionUrl: string, method = 'GET', producedType = 'application/json', cached = false) {
    super(apiExtensionUrl, method, producedType);
    this.cached = cached;
  }

  execute<T>(): Promise<T> {
    if (this.cached && hasCache(super.apiExtensionUrlWithParams())) {
      return getCache(super.apiExtensionUrlWithParams());
    }
    return super
      .execute()
      .then((response: Response) => responseCatcher(response, this.producedType))
      .then((response) => ((this.producedType && this.producedType === 'application/json') ? response?.json() : response))
      .then((responseData) => {
        if (this.cached) {
          insertCache(super.apiExtensionUrlWithParams(), responseData);
        }
        return responseData;
      })
      .catch(toJsonError);
  }
}

export class RestClientAuthenticated extends RestClient {
  constructor(apiExtensionUrl: string, method = 'GET', producedType = 'application/json', cached = false) {
    super(apiExtensionUrl, method, producedType, cached);
    this.headers({ Authorization: getAuthorization(), contentType: producedType });
  }
}
